import { Controller } from "@hotwired/stimulus";

// Manages lazy-loaded modals and the harvest entry drawer for the harvest plan
// and shipment calendars. Pattern matches field_cycle_line_item_modal_controller.js.
export default class extends Controller {
  connect() {
    this.boundHandleSubmitEnd = this.#handleSubmitEnd.bind(this);
    this.boundCloseTransientUi = this.#closeTransientUi.bind(this);
    document.addEventListener("turbo:submit-end", this.boundHandleSubmitEnd);
    document.addEventListener("turbo:before-cache", this.boundCloseTransientUi);
  }

  disconnect() {
    document.removeEventListener("turbo:submit-end", this.boundHandleSubmitEnd);
    document.removeEventListener(
      "turbo:before-cache",
      this.boundCloseTransientUi,
    );
  }

  openCreate(event) {
    event.stopPropagation();
    const modal = this.#findModal(event);
    if (modal) {
      this.#triggerLazyLoad(modal);
      modal.showModal();
    }
  }

  openEdit(event) {
    event.stopPropagation();
    const modal = this.#findModal(event);
    if (modal) {
      this.#triggerLazyLoad(modal);
      modal.showModal();
    }
  }

  openDelete(event) {
    event.stopPropagation();
    const modal = this.#findModal(event);
    if (modal) {
      modal.showModal();
    }
  }

  openView(event) {
    event.stopPropagation();
    const modal = this.#findModal(event);
    if (modal) {
      this.#triggerLazyLoad(modal);
      modal.showModal();
    }
  }

  // Shared dialog whose frame shows different content per trigger (Inventory
  // Balance cells): always (re)load the trigger's URL, since the same cell's
  // shipments may have changed since it was last opened.
  openFrame(event) {
    event.stopPropagation();
    const modal = this.#findModal(event);
    const url = event.currentTarget.dataset.lazyUrl;
    const turboFrame = modal?.querySelector("turbo-frame");
    if (!turboFrame || !url) return;

    // Show the initial spinner while another cell's content would still be up.
    this.frameSpinner ??= turboFrame.innerHTML;
    if (turboFrame.getAttribute("src") === url) {
      turboFrame.reload();
    } else {
      turboFrame.innerHTML = this.frameSpinner;
      turboFrame.src = url;
    }
    modal.showModal();
  }

  closeModal(event) {
    const modal = event.target.closest("dialog");
    if (modal) {
      modal.close();
    }
  }

  // Opens the right drawer and loads the harvest entry form for the given schedule
  openEntryDrawer(event) {
    event.preventDefault();
    event.stopPropagation();
    const url = event.currentTarget.dataset.entryUrl;
    const toggle = document.getElementById("harvest-entry-drawer-toggle");
    const frame = document.getElementById("harvest_entry_drawer_form");

    if (frame && url) {
      // Always reload to show the correct schedule's pre-filled form
      frame.src = url;
    }

    if (toggle) {
      toggle.checked = true;
    }
  }

  // --- Private ---

  #findModal(event) {
    const modalId = event.currentTarget.dataset.modalId;
    return document.getElementById(modalId);
  }

  #triggerLazyLoad(modal) {
    const url = modal.dataset.lazyModalUrlValue;
    if (url) {
      const turboFrame = modal.querySelector("turbo-frame");
      if (turboFrame && !turboFrame.src) {
        turboFrame.src = url;
      }
    }
  }

  #closeAllModals() {
    document.querySelectorAll("dialog[open]").forEach((dialog) => {
      dialog.close();
    });
  }

  #closeEntryDrawer() {
    const toggle = document.getElementById("harvest-entry-drawer-toggle");
    if (toggle) {
      toggle.checked = false;
    }
  }

  #handleSubmitEnd(event) {
    const { success, fetchResponse } = event.detail;
    if (!success) return;

    const status = fetchResponse?.response?.status;
    if (status && status >= 400) return;

    const form = event.target;

    // Handle drawer form submissions
    const drawerFrame = document.getElementById("harvest_entry_drawer_form");
    if (drawerFrame && drawerFrame.contains(form) && !form.closest("dialog")) {
      this.#closeEntryDrawer();
      this.#refreshCalendar();
      return;
    }

    // Handle modal form submissions
    if (form.hasAttribute("data-modal-no-refresh")) return;
    const dialog = form.closest("dialog");
    if (!dialog) return;
    if (!this.element.contains(dialog)) return;

    this.#closeAllModals();
    this.#refreshCalendar();
  }

  #refreshCalendar() {
    const frames = document.querySelectorAll(
      "turbo-frame#harvest_cycle_header, turbo-frame#harvest_overview_frame, turbo-frame#harvest_plan_frame, turbo-frame#harvest_shipments_frame",
    );
    if (!frames.length) return;

    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    frames.forEach((frame) => {
      frame.addEventListener(
        "turbo:frame-load",
        () => {
          window.scrollTo(scrollX, scrollY);
        },
        { once: true },
      );
      frame.src = window.location.href;
    });
  }

  #closeTransientUi() {
    this.#closeAllModals();
    this.#closeEntryDrawer();
  }
}
